import { __assign, __awaiter, __generator } from "tslib";
import download from 'downloadjs';
import ApiError from './ApiError';
import { HttpMethod } from './enums/http';
import { Mime } from './enums/mime';
import normalizeApiUrl from './helpers/normalizeApiUrl';
var X_API_VERSION = '1';
var DATE_OFFSET_TZ = (function () {
    var _a, _b, _c;
    var off = new Date().getTimezoneOffset();
    var tz = ((_c = (_b = (_a = window.Intl) === null || _a === void 0 ? void 0 : _a.DateTimeFormat()) === null || _b === void 0 ? void 0 : _b.resolvedOptions()) === null || _c === void 0 ? void 0 : _c.timeZone) || '';
    return "".concat(off, ",").concat(tz);
})();
var ApiClient = /** @class */ (function () {
    function ApiClient(options) {
        this.credentials = null;
        this.prefix = (options === null || options === void 0 ? void 0 : options.prefix) || '';
        this.baseUrl = normalizeApiUrl(options.baseUrl);
        this.credentials = options.credentials;
        this.language = options.language || window.navigator.language.substring(0, 2);
    }
    ApiClient.prototype.setBaseUrl = function (baseUrl) {
        this.baseUrl = normalizeApiUrl(baseUrl);
    };
    ApiClient.prototype.getBaseUrl = function () {
        return this.baseUrl;
    };
    ApiClient.prototype.setPrefix = function (prefix) {
        this.prefix = prefix;
    };
    ApiClient.prototype.getPrefix = function () {
        return this.prefix;
    };
    ApiClient.prototype.setLanguage = function (language) {
        this.language = language;
    };
    ApiClient.prototype.getLanguage = function () {
        return this.language;
    };
    ApiClient.prototype.setCredentials = function (credentials) {
        this.credentials = credentials;
    };
    ApiClient.prototype.getCredentials = function () {
        return this.credentials;
    };
    ApiClient.prototype.get = function (path, query) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.GET,
                        query: query,
                    })];
            });
        });
    };
    ApiClient.prototype.head = function (path, query) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.HEAD,
                        query: query,
                    })];
            });
        });
    };
    ApiClient.prototype.put = function (path, payload) {
        if (payload === void 0) { payload = {}; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.PUT,
                        body: payload,
                    })];
            });
        });
    };
    ApiClient.prototype.patch = function (path, payload) {
        if (payload === void 0) { payload = {}; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.PATCH,
                        body: payload,
                    })];
            });
        });
    };
    ApiClient.prototype.post = function (path, payload, contentType) {
        if (payload === void 0) { payload = {}; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.POST,
                        body: payload,
                        contentType: contentType,
                    })];
            });
        });
    };
    ApiClient.prototype.delete = function (path, payload) {
        if (payload === void 0) { payload = {}; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.request({
                        path: path,
                        method: HttpMethod.DELETE,
                        body: payload,
                    })];
            });
        });
    };
    ApiClient.prototype.request = function (_a) {
        var path = _a.path, method = _a.method, body = _a.body, _b = _a.contentType, contentType = _b === void 0 ? Mime.JSON : _b, query = _a.query;
        return __awaiter(this, void 0, void 0, function () {
            var init, queryString, accessToken, delegateUser, delegateCompany, _loop_1, key, url, res, status_1, contentType_1, _c, blob, res_1, data, err_1;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        queryString = '';
                        return [4 /*yield*/, this.credentials.getAccessToken()];
                    case 1:
                        accessToken = _d.sent();
                        init = {
                            method: method,
                            credentials: 'include',
                            headers: {
                                'X-Api-Version': X_API_VERSION,
                                Authorization: accessToken ? "Bearer ".concat(accessToken) : '',
                                Accept: 'application/json',
                                'Accept-Language': this.language,
                                'X-Client-Timezone': DATE_OFFSET_TZ,
                            },
                        };
                        if (method !== HttpMethod.GET) {
                            init = __assign(__assign({}, init), { headers: __assign(__assign({}, init.headers), { 'Content-Type': contentType }) });
                        }
                        return [4 /*yield*/, this.credentials.getDelegateUser()];
                    case 2:
                        delegateUser = _d.sent();
                        return [4 /*yield*/, this.credentials.getDelegateCompany()];
                    case 3:
                        delegateCompany = _d.sent();
                        if (delegateUser || delegateCompany) {
                            if (!query)
                                query = {};
                            if (delegateUser)
                                query.as_user = delegateUser;
                            if (delegateCompany)
                                query.as_company = delegateCompany;
                        }
                        if (typeof query === 'object' && Object.keys(query).length) {
                            queryString = '?';
                            _loop_1 = function (key) {
                                if (Array.isArray(query[key])) {
                                    queryString += query[key]
                                        .filter(function (q) { return q !== undefined; })
                                        .reduce(function (accum, item) { return (accum += "".concat(key, "=").concat(item, "&")); }, '');
                                }
                                else if (query[key] !== undefined) {
                                    queryString += "".concat(key, "=").concat(query[key], "&");
                                }
                            };
                            for (key in query) {
                                _loop_1(key);
                            }
                            queryString = queryString.substr(0, queryString.length - 1);
                        }
                        if (body && method !== HttpMethod.GET && method !== HttpMethod.HEAD) {
                            switch (contentType) {
                                case Mime.MULTIPART_FORM_DATA:
                                    init.body = body;
                                    // browser will generate an appropriate Content-Type header with the boundary
                                    delete init.headers['Content-Type'];
                                    break;
                                default:
                                    init.body = JSON.stringify(body);
                            }
                        }
                        _d.label = 4;
                    case 4:
                        _d.trys.push([4, 11, , 16]);
                        url = new URL(this.baseUrl);
                        url.pathname = "/".concat(this.prefix, "/").concat(path).replace(/\/+/g, '/');
                        return [4 /*yield*/, fetch("".concat(url.toString()).concat(queryString), init)];
                    case 5:
                        res = _d.sent();
                        status_1 = res.status;
                        if (!(200 <= status_1 && status_1 < 400)) return [3 /*break*/, 10];
                        contentType_1 = (res.headers.get('content-type') || '').split(';')[0];
                        _c = contentType_1;
                        switch (_c) {
                            case Mime.CSV: return [3 /*break*/, 6];
                            case Mime.SVG: return [3 /*break*/, 8];
                        }
                        return [3 /*break*/, 9];
                    case 6: return [4 /*yield*/, res.blob()];
                    case 7:
                        blob = _d.sent();
                        return [2 /*return*/, download(blob, 'bookings', contentType_1)];
                    case 8: return [2 /*return*/, res.text()];
                    case 9: return [2 /*return*/, res.json()];
                    case 10: throw res;
                    case 11:
                        res_1 = _d.sent();
                        data = void 0;
                        _d.label = 12;
                    case 12:
                        _d.trys.push([12, 14, , 15]);
                        return [4 /*yield*/, res_1.json()];
                    case 13:
                        data = _d.sent();
                        return [3 /*break*/, 15];
                    case 14:
                        err_1 = _d.sent();
                        return [3 /*break*/, 15];
                    case 15: throw new ApiError({ status: res_1.status, data: data });
                    case 16: return [2 /*return*/];
                }
            });
        });
    };
    return ApiClient;
}());
export { ApiClient };
